import {
  useCompleteHarnessOAuthAuthorization,
  useConnectHarnessCredential,
  useCreateHarnessOAuthAuthorization,
  useHarnessOAuthAuthorization,
} from "@/api/harnessCredentials";
import { isApiError } from "@/api/utils";
import {
  NESTED_DIALOG_Z,
  paneFieldInputClassName,
} from "@/components/AdminPanel/Organization/components/manage-pane-primitives";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useEnterprisePlan } from "@/hooks/useEnterprisePlan";
import ClaudeLogoIcon from "@/svg/brand/claude-logo-icon.svg?react";
import OpenAILogoIcon from "@/svg/brand/openai-logo-icon.svg?react";
import { cn } from "@/utils";
import {
  HarnessCredential,
  HarnessCredentialKind,
  HarnessCredentialProvider,
  HarnessOAuthAuthorization,
} from "@cohesive/api-client";
import { Spinner } from "@cohesive/ui-components";
import { ArrowUpRight, Check, Copy } from "lucide-react";
import {
  useEffect,
  useState,
  type ComponentType,
  type ReactNode,
  type SVGProps,
} from "react";

export const MODEL_PROVIDERS: {
  id: HarnessCredentialProvider;
  /** The company, where the dialog sends the user to sign in. */
  name: string;
  /** The subscription as Settings lists it. */
  label: string;
  /** The agent whose chats the subscription runs. */
  agentLabel: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  placeholder: string;
  /** What the subscription is called where the user bought it. */
  subscriptionLabel: string;
}[] = [
  {
    id: "anthropic",
    name: "Anthropic",
    label: "Claude",
    agentLabel: "Claude Code",
    icon: ClaudeLogoIcon,
    placeholder: "sk-ant-…",
    subscriptionLabel: "Claude Pro or Max",
  },
  {
    id: "openai",
    name: "OpenAI",
    label: "ChatGPT",
    agentLabel: "ChatGPT",
    icon: OpenAILogoIcon,
    placeholder: "sk-proj-…",
    subscriptionLabel: "ChatGPT Plus or Pro",
  },
];

export type ModelProvider = (typeof MODEL_PROVIDERS)[number];

export const KIND_LABELS: Record<HarnessCredentialKind, string> = {
  api_key: "API Key",
  subscription: "Subscription",
};

/** The server's own sentence for a refusal, which already says what to do next. */
function errorMessage(error: unknown, fallback: string) {
  const data = isApiError(error)
    ? error.data
    : (error as { response?: { data?: unknown } })?.response?.data;
  const message = (data as { error?: { message?: string } } | undefined)?.error
    ?.message;
  return message || fallback;
}

type Step =
  | { name: "intro" }
  | { name: "apiKey" }
  | {
      name: "signIn";
      authorization: HarnessOAuthAuthorization;
      /** The browser refused the provider's window, so the user opens it from here. */
      blocked: boolean;
    }
  | { name: "connected"; email?: string };

/** The provider's mark on a tile, as Settings' connector detail shows one. */
function MarkTile({
  provider,
  size = "lg",
}: {
  provider: ModelProvider;
  size?: "lg" | "sm";
}) {
  const Icon = provider.icon;
  return (
    <span
      aria-hidden
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-md border border-n-10 bg-n-05 text-n-90",
        size === "lg" ? "size-12" : "size-9",
      )}
    >
      <Icon className={size === "lg" ? "size-6" : "size-[18px]"} />
    </span>
  );
}

/** Below the body: a quiet swap on the left, the step's verbs on the right; stacked on a phone. */
function Footer({
  aside,
  children,
}: {
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex justify-center sm:justify-start">{aside}</div>
      <div className="flex flex-col-reverse gap-2 sm:flex-row">{children}</div>
    </div>
  );
}

function QuietLink({
  onClick,
  children,
}: {
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="cursor-pointer text-sm text-n-60 underline decoration-n-30 underline-offset-4 transition-colors hover:text-n-90"
    >
      {children}
    </button>
  );
}

function ErrorLine({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="text-sm text-lc-blocked">
      {children}
    </p>
  );
}

function StatusLine({ children }: { children: ReactNode }) {
  return (
    <p role="status" className="flex items-center gap-2 text-sm text-n-60">
      <span aria-hidden className="inline-flex">
        <Spinner size="xs" />
      </span>
      {children}
    </p>
  );
}

/** Opened before the request so the browser counts it as the click's; navigated once it lands. */
function openProviderWindow() {
  const opened = window.open("", "_blank");
  if (opened) opened.opener = null;
  return opened;
}

/**
 * Connect a model provider credential. Opens on the subscription: what connecting does, then one
 * verb that starts the provider's sign-in. Each step swaps in place — the sign-in (a code to paste
 * back for Claude, a code to enter at OpenAI for ChatGPT), an API key as the quiet alternative
 * (offered to enterprise orgs only), and a connected beat whose Done closes it.
 *
 * Shared by Settings › Model providers and the composer, whose Connect buttons open it on the
 * agent's provider. `onConnected` fires the moment the credential is stored; the credential
 * mutations invalidate the capabilities, which is what unblocks a composer waiting on it.
 *
 * `paste_code` (Anthropic) shows the code on its own page for the user to bring back here.
 * `device_code` (ChatGPT) takes a code from the user instead, because OpenAI pins its redirect to
 * localhost and a web app has no way to receive one — so we show the code and poll.
 */
export function ConnectProviderDialog({
  provider,
  open,
  onOpenChange,
  onConnected,
  className,
}: {
  provider: ModelProvider;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConnected?: () => void;
  /** Stacking for the host: Settings nests it over the manage modal. */
  className?: string;
}) {
  const [step, setStep] = useState<Step>({ name: "intro" });
  const offerApiKey = useEnterprisePlan();
  const [error, setError] = useState<string>();
  const start = useCreateHarnessOAuthAuthorization();
  const complete = useCompleteHarnessOAuthAuthorization();
  const connect = useConnectHarnessCredential();

  useEffect(() => {
    if (open) {
      setStep({ name: "intro" });
      setError(undefined);
    }
  }, [open, provider.id]);

  const go = (next: Step) => {
    setError(undefined);
    setStep(next);
  };
  const landed = (credential?: HarnessCredential) => {
    go({ name: "connected", email: credential?.account_email });
    onConnected?.();
  };

  const startSignIn = async () => {
    const opened = openProviderWindow();
    try {
      const authorization = await start.mutateAsync(provider.id);
      if (opened) opened.location.href = authorization.authorize_url;
      go({ name: "signIn", authorization, blocked: !opened });
    } catch (startError) {
      opened?.close();
      setError(
        errorMessage(startError, `Couldn't reach ${provider.name}. Try again.`),
      );
    }
  };

  // A connect in flight cannot be cancelled, so dismissing would only hide it: the credential still
  // gets stored. Hold the dialog until it lands either way.
  const pending = start.isPending || complete.isPending || connect.isPending;

  return (
    <Dialog open={open} onOpenChange={(next) => !pending && onOpenChange(next)}>
      <DialogContent
        className={cn(
          "gap-6 rounded-xl p-6 sm:max-w-[32rem] sm:p-8",
          className ?? NESTED_DIALOG_Z,
        )}
        overlayClassName={className ?? NESTED_DIALOG_Z}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        {step.name === "intro" && (
          <IntroStep
            provider={provider}
            error={error}
            pending={start.isPending}
            onStart={startSignIn}
            onApiKey={offerApiKey ? () => go({ name: "apiKey" }) : undefined}
          />
        )}
        {step.name === "signIn" &&
          (step.authorization.mode === "device_code" ? (
            <DeviceCodeStep
              provider={provider}
              authorization={step.authorization}
              blocked={step.blocked}
              onConnected={landed}
              onRestart={() => go({ name: "intro" })}
            />
          ) : (
            <PasteCodeStep
              provider={provider}
              authorization={step.authorization}
              blocked={step.blocked}
              pending={complete.isPending}
              error={error}
              onSubmit={async (code) => {
                setError(undefined);
                try {
                  const result = await complete.mutateAsync({
                    authorizationId: step.authorization.id,
                    code,
                  });
                  landed(result.harnessCredential);
                } catch (completeError) {
                  setError(
                    errorMessage(
                      completeError,
                      "That code wasn't accepted. Copy the whole code and try again.",
                    ),
                  );
                }
              }}
              onBack={() => go({ name: "intro" })}
            />
          ))}
        {step.name === "apiKey" && (
          <ApiKeyStep
            provider={provider}
            pending={connect.isPending}
            error={error}
            onSubmit={async (secret) => {
              setError(undefined);
              try {
                landed(
                  await connect.mutateAsync({
                    provider: provider.id,
                    kind: "api_key",
                    secret,
                  }),
                );
              } catch (connectError) {
                setError(
                  errorMessage(
                    connectError,
                    `${provider.name} didn't accept that key. Check it and try again.`,
                  ),
                );
              }
            }}
            onSubscription={() => go({ name: "intro" })}
          />
        )}
        {step.name === "connected" && (
          <ConnectedStep
            provider={provider}
            email={step.email}
            onDone={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function IntroStep({
  provider,
  error,
  pending,
  onStart,
  onApiKey,
}: {
  provider: ModelProvider;
  error?: string;
  pending: boolean;
  onStart: () => void;
  onApiKey?: () => void;
}) {
  const facts = [
    `Consumes your existing ${provider.label} subscription usage, instead of Cohesive credits`,
    "Disconnect any time in Settings › Model providers",
  ];
  return (
    <>
      <div className="flex flex-col gap-4">
        <MarkTile provider={provider} />
        <div className="flex flex-col gap-2">
          <p className="flex items-center gap-2 font-mono text-2xs font-medium uppercase tracking-[0.08em] text-n-50">
            <span
              aria-hidden
              className="size-1.5 shrink-0 rounded-full bg-rust"
            />
            {provider.subscriptionLabel}
          </p>
          <DialogTitle className="text-2xl leading-tight font-medium text-n-90">
            Connect {provider.label}
          </DialogTitle>
          <DialogDescription className="text-md leading-relaxed text-n-60">
            {provider.agentLabel} chats you start run on your{" "}
            {provider.subscriptionLabel} plan.
          </DialogDescription>
        </div>
        <ul className="flex flex-col gap-2">
          {facts.map((fact) => (
            <li key={fact} className="flex items-start gap-3 text-sm text-n-70">
              <Check className="mt-0.5 size-3.5 shrink-0 text-n-40" />
              {fact}
            </li>
          ))}
        </ul>
        {error && <ErrorLine>{error}</ErrorLine>}
      </div>
      <Footer
        aside={
          onApiKey && (
            <QuietLink onClick={onApiKey}>Use an API key instead</QuietLink>
          )
        }
      >
        <Button variant="accent" isLoading={pending} onClick={onStart}>
          {error ? "Try again" : `Connect ${provider.label}`}
          <ArrowUpRight className="size-4" />
        </Button>
      </Footer>
    </>
  );
}

/** The functional steps' head: the mark, small, beside the plain title and what to do. */
function StepHead({
  provider,
  title,
  children,
}: {
  provider: ModelProvider;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <MarkTile provider={provider} size="sm" />
      <div className="flex min-w-0 flex-col gap-1">
        <DialogTitle className="text-lg font-medium text-n-90">
          {title}
        </DialogTitle>
        <DialogDescription className="text-sm leading-relaxed text-n-60">
          {children}
        </DialogDescription>
      </div>
    </div>
  );
}

function ProviderLink({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-sm text-n-70 underline decoration-n-30 underline-offset-4 transition-colors hover:text-n-90"
    >
      {children}
      <ArrowUpRight className="size-3.5" />
    </a>
  );
}

function PasteCodeStep({
  provider,
  authorization,
  blocked,
  pending,
  error,
  onSubmit,
  onBack,
}: {
  provider: ModelProvider;
  authorization: HarnessOAuthAuthorization;
  blocked: boolean;
  pending: boolean;
  error?: string;
  onSubmit: (code: string) => void;
  onBack: () => void;
}) {
  const [code, setCode] = useState("");
  return (
    <>
      <StepHead provider={provider} title={`Sign in to ${provider.label}`}>
        {blocked
          ? `Your browser blocked the ${provider.label} window. Open it, sign in, then copy the code it shows and paste it here.`
          : `A ${provider.label} window opened. Sign in, copy the code it shows, and paste it here.`}
      </StepHead>
      <form
        noValidate
        className="flex flex-col gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (code.trim()) onSubmit(code.trim());
        }}
        id="harness-oauth-form"
      >
        <Label htmlFor="harness-oauth-code">Code from {provider.label}</Label>
        <Input
          id="harness-oauth-code"
          autoComplete="off"
          spellCheck={false}
          data-1p-ignore
          data-lpignore="true"
          className={paneFieldInputClassName}
          value={code}
          aria-invalid={!!error}
          onChange={(event) => setCode(event.target.value)}
        />
        {pending ? (
          <StatusLine>Checking the code with {provider.name}…</StatusLine>
        ) : error ? (
          <ErrorLine>{error}</ErrorLine>
        ) : (
          <ProviderLink href={authorization.authorize_url}>
            {blocked
              ? `Open ${provider.label}`
              : `Open the ${provider.label} window again`}
          </ProviderLink>
        )}
      </form>
      <Footer>
        <Button variant="ghost" disabled={pending} onClick={onBack}>
          Back
        </Button>
        <Button
          type="submit"
          form="harness-oauth-form"
          disabled={!code.trim() || pending}
        >
          {error ? "Try again" : "Finish connecting"}
        </Button>
      </Footer>
    </>
  );
}

function DeviceCodeStep({
  provider,
  authorization,
  blocked,
  onConnected,
  onRestart,
}: {
  provider: ModelProvider;
  authorization: HarnessOAuthAuthorization;
  blocked: boolean;
  onConnected: (credential: HarnessCredential) => void;
  onRestart: () => void;
}) {
  const polled = useHarnessOAuthAuthorization(authorization);
  const [copied, setCopied] = useState(false);
  const credential = polled.data?.harnessCredential;

  // The device flow lands without anything happening in this dialog: the user finishes at the
  // provider and the poll finds the credential.
  useEffect(() => {
    if (credential) onConnected(credential);
  }, [credential, onConnected]);

  // Only the provider's own verdict ends this: the server records it as a failed status, and a
  // device code expires on its own. A fetch that failed says something about the network, and
  // retracting a live sign-in over one would throw away a code the user may already have entered.
  const failed = polled.data?.authorization.status === "failed";
  const host = new URL(authorization.authorize_url).host;

  return (
    <>
      <StepHead provider={provider} title={`Sign in to ${provider.label}`}>
        {blocked
          ? `Open ${host}, sign in to ${provider.label}, and enter this code.`
          : `Sign in to ${provider.label} in the window that opened and enter this code.`}
      </StepHead>
      <div className="flex flex-col items-center gap-3 rounded-lg border border-n-10 bg-n-05 px-4 py-5">
        <p
          aria-label="Your code"
          className="font-mono text-3xl font-medium tracking-[0.2em] text-n-90 select-all"
        >
          {authorization.user_code}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard
                ?.writeText(authorization.user_code ?? "")
                .then(() => setCopied(true));
            }}
            className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-n-70 transition-colors hover:text-n-90"
          >
            {copied ? (
              <Check className="size-3.5" />
            ) : (
              <Copy className="size-3.5" />
            )}
            {copied ? "Copied" : "Copy code"}
          </button>
          <ProviderLink href={authorization.authorize_url}>
            Open {host}
          </ProviderLink>
        </div>
      </div>
      {failed ? (
        <ErrorLine>
          That sign-in didn&apos;t finish, or the code expired. Start again for
          a new code.
        </ErrorLine>
      ) : (
        <StatusLine>Waiting for you to approve in {provider.label}…</StatusLine>
      )}
      <Footer>
        {failed ? (
          <Button onClick={onRestart}>Start again</Button>
        ) : (
          <Button variant="ghost" onClick={onRestart}>
            Back
          </Button>
        )}
      </Footer>
    </>
  );
}

function ApiKeyStep({
  provider,
  pending,
  error,
  onSubmit,
  onSubscription,
}: {
  provider: ModelProvider;
  pending: boolean;
  error?: string;
  onSubmit: (secret: string) => void;
  onSubscription: () => void;
}) {
  const [secret, setSecret] = useState("");
  return (
    <>
      <StepHead provider={provider} title={`Use an ${provider.name} API key`}>
        {provider.agentLabel} chats you start run on this key instead of a
        subscription. It&apos;s checked with {provider.name} before it&apos;s
        stored.
      </StepHead>
      <form
        noValidate
        id="harness-api-key-form"
        className="flex flex-col gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (secret.trim()) onSubmit(secret.trim());
        }}
      >
        <Label htmlFor="harness-credential-secret">API key</Label>
        <Input
          id="harness-credential-secret"
          type="password"
          autoComplete="off"
          spellCheck={false}
          data-1p-ignore
          data-lpignore="true"
          className={paneFieldInputClassName}
          placeholder={provider.placeholder}
          value={secret}
          aria-invalid={!!error}
          onChange={(event) => setSecret(event.target.value)}
        />
        {pending ? (
          <StatusLine>Checking the key with {provider.name}…</StatusLine>
        ) : error ? (
          <ErrorLine>{error}</ErrorLine>
        ) : (
          <p className="text-sm text-n-50">
            Cohesive stores it encrypted and never writes it into your sandbox.
          </p>
        )}
      </form>
      <Footer
        aside={
          <QuietLink onClick={onSubscription}>
            Use your subscription instead
          </QuietLink>
        }
      >
        <Button
          type="submit"
          form="harness-api-key-form"
          disabled={!secret.trim() || pending}
        >
          {error ? "Try again" : "Connect key"}
        </Button>
      </Footer>
    </>
  );
}

function ConnectedStep({
  provider,
  email,
  onDone,
}: {
  provider: ModelProvider;
  email?: string;
  onDone: () => void;
}) {
  return (
    <>
      <div className="flex flex-col gap-4">
        <MarkTile provider={provider} />
        <div className="flex flex-col gap-2">
          <DialogTitle className="text-2xl leading-tight font-medium text-n-90">
            {provider.label} is connected
          </DialogTitle>
          <DialogDescription className="text-md leading-relaxed text-n-60">
            New {provider.agentLabel} chats can run on it now.
          </DialogDescription>
        </div>
        {email && (
          <p className="flex items-center gap-2 text-sm text-n-70">
            <Check className="size-3.5 shrink-0 text-lc-done" />
            Signed in as {email}
          </p>
        )}
      </div>
      <Footer>
        <Button onClick={onDone}>Done</Button>
      </Footer>
    </>
  );
}
